// third-party
import { FormattedMessage } from "react-intl";

// project imports
import SECTIONS, { buildYearTabs, tabHref } from "./sectionTabs";
import { getAdmin } from "./Admin";

// assets
import {
  IconUsers,
  IconList,
  IconSquareCheck,
  IconChartArcs,
  IconSearch,
  IconPlant,
  IconTrophy,
  IconHelp,
  IconShieldLock,
  IconBuildingBank,
  IconWorld,
} from "@tabler/icons";

/*
 * Adapts a section's tab icon — a lucide one, from sectionTabs.js — to the way
 * the sidebar renders its glyphs.
 *
 * NavItem and NavCollapse pass `stroke`, which for their tabler icons is the
 * stroke WIDTH. Lucide reads `stroke` as the paint COLOUR and spreads its props
 * onto the <svg> last, so passing that same `stroke={1.5}` to a lucide icon
 * would overwrite `stroke="currentColor"` with an invalid paint and the glyph
 * would come out blank. This maps the one prop that differs and forwards the
 * rest, which is what lets a section's own tab icons — the same glyphs the
 * in-page strip shows — sit in the sidebar beside the tabler ones.
 *
 * Called once per icon, and the result kept, so the component identity is
 * stable. A new wrapper on each call would be a new component TYPE as far as
 * React is concerned, and every sub-menu glyph would unmount and remount on each
 * rebuild of this file's menu.
 */
const glyphs = new Map();
const navGlyph = (Icon) => {
  if (!glyphs.has(Icon)) {
    glyphs.set(Icon, function NavGlyph({ stroke, color, size, style }) {
      return <Icon size={size} color={color} strokeWidth={stroke ?? 2} style={style} />;
    });
  }
  return glyphs.get(Icon);
};

/*
 * A section entry's children — the section's own tabs, expandable in the
 * sidebar.
 *
 * Read from sectionTabs.js rather than typed out again here. That file is the
 * single declaration of the tabs (the strip inside each page renders the same
 * list), so a second copy here could drift out of step with it — which is
 * exactly how the Learnings section ended up with two tabs on /Resources.
 *
 * Read-only: nothing in this file writes to sectionTabs.js, and the in-page
 * strip is not affected by anything below.
 *
 * Both the id and the url come from tabHref rather than the bare path. For every
 * section in the app today that is now identity: no tab in SECTIONS carries a
 * `tab` or a `year` any more, because the module views that used to share
 * Community's path have moved into the module's own page. It stays because the id
 * is what NavItem keys, checks its selection against and dispatches as
 * `activeItem`, and a path-only id would hand two query-keyed tabs one id the
 * moment one is added back. QIC's year tabs are why that matters — their urls
 * carry `?year=`.
 *
 * No ordering requirement survives here. It used to be load-bearing for
 * Community: NavItem dispatches `activeItem([id])`, which REPLACES the selection,
 * so when two children matched one URL the last to run its effect won — and
 * Dashboard's query-less url matched by path segment even while the url described
 * a sibling, so Dashboard had to precede the views that shared its path or the
 * sidebar would sit lit on Dashboard while the header card lit the view. Those
 * views have left this list, no two remaining tabs share a path, and no entry
 * below depends on where it sits.
 */
const tabsOf = (sectionId) =>
  (SECTIONS.find((section) => section.id === sectionId)?.tabs ?? []).map(
    (tab) => ({
      id: tabHref(tab).replace(/^\//, ""),
      title: <FormattedMessage id={tab.label} />,
      type: "item",
      url: tabHref(tab),
      // Each sub-item carries its tab's glyph, so a tab looks the same in the
      // sidebar as it does in the strip. A tab without one falls back to the
      // bullet NavItem draws for icon-less items.
      icon: tab.icon ? navGlyph(tab.icon) : undefined,
      breadcrumbs: false,
      isbeta: false,
    })
  );

// constant
const icons = {
  IconUsers,
  IconList,
  IconSquareCheck,
  IconChartArcs,
  IconSearch,
  IconPlant,
  IconTrophy,
  IconHelp,
  IconShieldLock,
  IconBuildingBank,
  IconWorld,
};

// ==============================|| DHRUVA - SECTION NAVIGATION ||============================== //
/*
 * One entry per section, matching the "Simplified navigation" design, and each
 * one expandable: `type: "collapse"` with that section's tabs as its children,
 * so the sub-navigation the design moved into the page is also reachable
 * without leaving the sidebar. The in-page tab strip is untouched and still
 * renders the same tabs — these are the same destinations reached two ways.
 *
 * A `collapse` entry is not itself a link, so clicking one in the sidebar
 * navigates via NavCollapse rather than through a <Link>. NavCollapse sends that
 * click to the entry's first child. On the six tabs-driven sections `url` below
 * therefore names that same page and is asserted equal to it; QIC is the
 * exception, see its own note.
 *
 * Two entries stay plain `item`s: Sector and OmniWealth, because the design
 * renders no strip for a section with a single destination — sectionTabs.js has
 * an entry for neither, so there is nothing to nest. Neither is a "section" in
 * that file's sense; each is one page.
 *
 * The `url` each entry carries today — QIC's is a record of the page the entry
 * opens while it is still flat, before its years arrive:
 *
 *   Community  -> /CommunityDashboard
 *   OmniWealth -> /OmniWealth           (a plain item: this one IS the link)
 *   Sector     -> /SectorWatchList      (a plain item: this one IS the link)
 *   Satellite  -> /SateliteWatchlist
 *   Core       -> /CoreWatchlist
 *   Research   -> /ResearchReport
 *   Learnings  -> /UniverseCoach
 *   QIC        -> /qic/dashboard        (flat until its years arrive)
 *   Help       -> /contact-us
 *
 * The design merges ~18 old menu items into eight of these, OmniWealth excepted:
 * it is a page that did not exist when that design was drawn and is not a
 * survivor of the merge. Everything the eight list is
 * back in the sidebar as a child of its section, and Community's are its two
 * pills — Dashboard and Events — because `children` is built from that section's
 * own row by tabsOf. Hall of Fame and TF Masters are views of the Dashboard
 * rather than pages of their own, reached from the switcher inside the module's
 * page; Events took Hall of Fame's place on this row when the two traded places.
 *
 * The only page the design drops that this file does not link is Core Audit,
 * which lives at its own route. Nothing else was deleted.
 *
 * Admin sits in its own untitled group so the sidebar renders the design's
 * divider above it.
 */

/*
 * The seven sections whose sub-menu is fixed, built once at module load.
 *
 * Hoisting them out of the assembly below is what keeps the years' arrival from
 * disturbing them. QIC's children come from the API, so the group has to be
 * rebuilt when they land — and if that rebuild produced new objects for these
 * too, every one of their NavCollapses would see a new `menu.children`, re-run
 * the effect that closes a section, and snap shut whatever the user had open.
 * Only QIC's own entry changes, so only QIC reacts.
 */
const community = {
  id: "Community",
  title: <FormattedMessage id="Community" />,
  type: "collapse",
  url: "/CommunityDashboard",
  icon: icons.IconUsers,
  breadcrumbs: false,
  isbeta: false,
  children: tabsOf("community"),
};

/*
 * The second plain `item`, for the same reason Sector is one: the page is a
 * single destination with no sub-navigation, so there is nothing to nest and no
 * entry in sectionTabs.js for `tabsOf` to read.
 *
 * Its glyph is Tabler's bank, which is the columns mark the page's own header
 * wears (lucide's Landmark — the sidebar renders Tabler icons and the page renders
 * lucide ones). Every other icon this file imports belongs to a section already,
 * so reusing one would put the same glyph on two sidebar rows.
 *
 * Sits between Community and Sector. The design that named this page draws it in
 * a group of its own above the stocks entries, and Sector is the first of those;
 * the sidebar has no group captions, so position is the only thing that can say
 * it — and this is where it says it.
 */
const omnivealth = {
  id: "OmniWealth",
  title: <FormattedMessage id="OmniWealth" />,
  type: "item",
  url: "/OmniWealth",
  icon: icons.IconBuildingBank,
  breadcrumbs: false,
  isbeta: false,
};

const sector = {
  id: "Sector",
  title: <FormattedMessage id="Sector" />,
  type: "item",
  url: "/SectorWatchList",
  icon: icons.IconList,
  breadcrumbs: false,
  isbeta: false,
};

const satellite = {
  id: "Satellite",
  title: <FormattedMessage id="Satellite" />,
  type: "collapse",
  url: "/SateliteWatchlist",
  icon: icons.IconSquareCheck,
  breadcrumbs: false,
  isbeta: false,
  children: tabsOf("satellite"),
};

const core = {
  id: "Core",
  title: <FormattedMessage id="Core" />,
  type: "collapse",
  url: "/CoreWatchlist",
  icon: icons.IconChartArcs,
  breadcrumbs: false,
  isbeta: false,
  children: tabsOf("core"),
};

// Research is hidden from the sidebar for now. Commented, not removed: the
// section still exists in sectionTabs.js and /ResearchReport still resolves, so
// re-enabling is a matter of uncommenting this block and its entry below.
// const research = {
//   id: "Research",
//   title: <FormattedMessage id="Research" />,
//   type: "collapse",
//   url: "/ResearchReport",
//   icon: icons.IconSearch,
//   breadcrumbs: false,
//   isbeta: false,
//   children: tabsOf("research"),
// };

/*
 * Global Investing sits above Growth Hub. A single destination, so it is an
 * `item` rather than a `collapse` — there is no second page for a submenu to
 * hold. Its glyph is unique among these rows, which the file's own rule requires:
 * a second row wearing an icon another row already wears reads as the same place.
 *
 * The page behind it is built (dashboard/GlobalInvesting); the url and this
 * entry are as they were when it was a placeholder.
 */
const globalInvesting = {
  id: "Global Investing",
  title: <FormattedMessage id="Global Investing" />,
  type: "item",
  url: "/GlobalInvesting",
  icon: icons.IconWorld,
  breadcrumbs: false,
  isbeta: false,
};

const learnings = {
  id: "Growth Hub",
  title: <FormattedMessage id="Growth Hub" />,
  type: "collapse",
  url: "/UniverseCoach",
  icon: icons.IconPlant,
  breadcrumbs: false,
  isbeta: false,
  children: tabsOf("Growth Hub"),
};

const help = {
  id: "Help",
  title: <FormattedMessage id="Help" />,
  type: "collapse",
  url: "/contact-us",
  icon: icons.IconHelp,
  breadcrumbs: false,
  isbeta: false,
  children: tabsOf("help"),
};

const QIC_SECTION = SECTIONS.find((section) => section.id === "qic");

/*
 * QIC's sub-menu: one child per event year — the SAME list the in-page strip
 * shows, built by the same function from the same data, so the sidebar and the
 * strip cannot disagree about which years exist.
 *
 * The years live in the database, which is why this is the one entry that has to
 * be built rather than declared.
 *
 * `pathname` is empty on purpose. In the strip it keeps a year click on the page
 * you are reading; here there is no "current page" to preserve, so the tabs fall
 * back to the section's canonical path and a sidebar click lands on the QIC
 * dashboard, filtered to that year.
 *
 * `events` — the same fetch's event rows — is what carries each year's `status`,
 * and so which one is live. Passed through rather than re-derived, so the dot
 * the sidebar draws marks the same championship the strip's dot marks.
 */
const qicCache = new Map();

// Built once per build and handed to both the caches and the children, so there
// is a single expression of what QIC's sub-menu contains. With no years it is
// empty rather than absent, which is what keeps QIC flat until the API answers.
const qicTabs = (years, events) => (years.length ? buildYearTabs(QIC_SECTION, years, "", events) : []);

/*
 * What makes QIC's entry change, and so its cache key.
 *
 * Not the year list alone: a refetch can flip a championship's status without
 * adding or removing a year, and a key of years only would serve the entry built
 * before that — a stale sub-menu with no dot on the year that just went live.
 * Derived from the tabs themselves so there is no second copy of buildYearTabs'
 * liveness rule to drift out of step with it.
 */
const qicKey = (tabs) => tabs.map((tab) => `${tab.year}:${tab.live ? "live" : "-"}`).join(",");

const qicEntry = (years = [], events = []) => {
  const tabs = qicTabs(years, events);
  const key = qicKey(tabs);
  if (!qicCache.has(key)) {
    const children = tabs.map((tab) => ({
      id: `qic-${tab.year}`,
      title: <FormattedMessage id={tab.label} />,
      type: "item",
      url: tabHref(tab),
      icon: tab.icon ? navGlyph(tab.icon) : undefined,
      // The running championship. NavItem draws the dot; the flag is data, the
      // same way the strip receives it and decides for itself how to mark it.
      live: tab.live,
      breadcrumbs: false,
      isbeta: false,
    }));

    qicCache.set(key, {
      id: "QIC",
      title: <FormattedMessage id="QIC" />,
      // Flat until the years arrive, so it is never an expander that opens
      // nothing. As a plain item its url is live, and it is the destination the
      // entry has always had.
      type: children.length ? "collapse" : "item",
      url: "/qic/dashboard",
      icon: icons.IconTrophy,
      breadcrumbs: false,
      isbeta: false,
      ...(children.length ? { children } : {}),
    });
  }
  return qicCache.get(key);
};

/*
 * The sidebar's main group.
 *
 * A function, not a constant, because QIC's children are not knowable until the
 * database answers. The result is cached on what QIC's entry is built from so
 * the object — and every entry inside it — keeps its identity between renders.
 *
 * That identity is load-bearing twice over. NavCollapse's effect lists
 * `menu.children` as a dependency and its first act is to close the section, so
 * a rebuilt array would snap the sidebar shut whatever the user had just opened.
 * And NavGroup stores the group in state, so a new object on every render would
 * have it setting state on each pass.
 */
const cache = new Map();

const buildMain = (years = [], events = []) => {
  const key = qicKey(qicTabs(years, events));
  if (!cache.has(key)) {
    cache.set(key, {
      id: "main",
      title: "",
      type: "group",
      children: [
        community,
        sector,
        satellite,
        core,
        // research,
        globalInvesting,
        omnivealth,
        learnings,
        qicEntry(years, events),
        help,
      ],
    });
  }
  return cache.get(key);
};

/*
 * The page an expander opens: its first child's page, and when that child is
 * itself an expander with a page of its own, that page.
 *
 * The precedence is the rule the sections have always used — the child is
 * consulted BEFORE the entry itself — and it is load-bearing. Admin.js gives
 * `qic` the url /User_Management and `Cloud` the url /qic_event, both left over
 * from the entry above them in that file and both wrong. Nothing should start
 * believing them, and nothing does while the first child is what answers.
 *
 * Recursive because the sidebar now nests three deep: the Admin entry's first
 * child is the url-less `Help` collapse, so only descending past it finds a page.
 *
 * Exported because NavCollapse navigates on it, and because it is the one piece
 * of this file pure enough to test directly.
 */
export const landingUrl = (entry) => {
  const [first] = entry.children ?? [];
  return first ? first.url ?? landingUrl(first) : entry.url;
};

/*
 * The sidebar's admin group: one entry, `Admin`, opening the first page of the
 * admin tree.
 *
 * It used to expand into that whole tree, so all 20 admin pages were reachable
 * from the sidebar. The user asked for that sub-menu to be removed, so the
 * in-page rail above the admin pages is now the only place the tree is rendered
 * — which is also what keeps the two from being two renderings of one list.
 *
 * Memoised on the children array's identity rather than on the role, so it
 * rebuilds exactly when the tree does — a role change — and not when QIC's years
 * land. The identity still matters even though the entry no longer holds the
 * children: the sidebar stores this group in state, so an object built fresh per
 * call would have it setting state on every render. The same discipline as
 * qicEntry's cache.
 *
 * No `title` on purpose — NavGroup renders no caption for it, which is what puts
 * Admin below the design's divider on its own.
 */
const adminCache = new Map();

const buildAdmin = () => {
  const { children } = getAdmin();

  if (!adminCache.has(children)) {
    adminCache.set(children, {
      id: "admin-group",
      title: null,
      type: "group",
      children: [
        {
          id: "Admin",
          title: <FormattedMessage id="Admin" />,
          /*
           * An `item`, not a `collapse`. Its `url` is still DERIVED from the tree
           * rather than typed out, so the link cannot name a page the tree does not
           * have — and it is the page the expander already opened, so removing the
           * sub-menu does not change where clicking Admin goes.
           */
          type: "item",
          url: landingUrl({ children }),
          icon: icons.IconShieldLock,
          breadcrumbs: false,
          isbeta: false,
        },
      ],
    });
  }

  return adminCache.get(children);
};

export { buildMain, buildAdmin };
